/**
 * Phone number rules — one place, used by every form that takes a number.
 *
 * A valid number is 10 digits (0700102510). Numbers written in the
 * international form (+256 700 102510 / 256700102510) are accepted and
 * normalised to the local 10-digit form rather than rejected, because
 * plenty of existing records are stored that way.
 *
 * This catches what the data actually contains: 9-digit numbers missing a
 * digit, 11+ digit typos, two numbers crammed into one field separated by
 * a slash, and names typed into the phone box.
 */

export const PHONE_LENGTH = 10;

export const PHONE_HELPER_TEXT = '10 digits, e.g. 0700102510';

/**
 * Keeps only digits as the user types, so letters, slashes and spaces
 * never make it into the field in the first place. Capped at 12 so an
 * international 256... number still fits while runaway typing doesn't.
 *
 * A pasted "+256 700 102510" loses its "+" and spaces and becomes
 * 256700102510, which normalizePhone folds to 0700102510 — so pasting an
 * international number still works.
 */
export const sanitizePhoneInput = (raw: string): string =>
  raw.replace(/[^0-9]/g, '').slice(0, 12);

/** Spread onto a phone TextField to get the numeric keypad on mobile. */
export const PHONE_INPUT_PROPS = {
  inputMode: 'numeric' as const,
  pattern: '[0-9]*',
};

/** Strips spaces, dashes and brackets, and folds +256 into a leading 0. */
export const normalizePhone = (raw: string | null | undefined): string => {
  if (!raw) return '';
  let digits = String(raw).replace(/[^0-9]/g, '');
  // 256700102510 -> 0700102510
  if (digits.length === 12 && digits.startsWith('256')) {
    digits = `0${digits.slice(3)}`;
  }
  return digits;
};

/** True when the number is usable. Empty counts as valid — use `required` separately. */
export const isValidPhone = (raw: string | null | undefined): boolean => {
  const value = String(raw ?? '').trim();
  if (!value) return true;
  return normalizePhone(value).length === PHONE_LENGTH;
};

/**
 * Error text for a field, or '' when fine. Returning a string keeps call
 * sites simple: `const err = phoneError(v); if (err) { ...stop... }`
 */
export const phoneError = (
  raw: string | null | undefined,
  { required = false }: { required?: boolean } = {},
): string => {
  const value = String(raw ?? '').trim();
  if (!value) return required ? 'Phone number is required' : '';

  const digits = normalizePhone(value);
  if (/[a-zA-Z]/.test(value) && digits.length !== PHONE_LENGTH) {
    return 'That looks like a name, not a phone number';
  }
  if (value.includes('/') || value.includes(',')) {
    return 'Enter one number only';
  }
  if (digits.length !== PHONE_LENGTH) {
    return `Phone number must be ${PHONE_LENGTH} digits`;
  }
  return '';
};
